import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import {
  documentUrl,
  insertAtCursor,
  isWord,
  loadOffice,
  openDialog,
  scanTags,
  setReveal,
} from "./officeBridge";
import { fetchContext, getTokens, isAuthError, publishDraft, setTokens } from "./wordAddinApi";
import {
  cleanFieldName,
  fieldTag,
  filenameFromUrl,
  filterFields,
  signatureTag,
  summarizeTags,
} from "./wordAddinModel";

/*
 * The WHG Docs panel inside Word. Three jobs:
 *   Fields      — drop a template field tag at the cursor (existing or new)
 *   Signatures  — drop a signature / date / initials / printed-name tag for a signer
 *   Reveal tags — colour every tag in the document so it can be seen; the
 *                 colours are stripped again when the template is published
 */

const SIGNATURE_TEXT_COLOR = "#FFFFFF";

const Pane = styled.div`
  font-family: "Segoe UI", "Manrope", sans-serif;
  font-size: 13px;
  color: #0f172a;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-height: 100vh;
  box-sizing: border-box;
  background: #fff;
`;

const Brand = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  font-weight: 800;
  color: #5b21b6;
  letter-spacing: -0.01em;
`;

const Small = styled.div`
  font-size: 12px;
  color: #64748b;
  line-height: 1.45;
`;

const Section = styled.section`
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 10px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SectionTitle = styled.h3`
  margin: 0;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #475569;
`;

const Tabs = styled.div`
  display: flex;
  gap: 4px;
  border-bottom: 1px solid #e2e8f0;
`;

const Tab = styled.button`
  flex: 1;
  border: 0;
  background: none;
  padding: 8px 4px;
  font-weight: 700;
  font-size: 12px;
  color: ${(p) => (p.$active ? "#1e40af" : "#64748b")};
  border-bottom: 2px solid ${(p) => (p.$active ? "#1e40af" : "transparent")};
  cursor: pointer;
`;

const Input = styled.input`
  width: 100%;
  box-sizing: border-box;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  padding: 7px 9px;
  font-size: 13px;
`;

const Select = styled.select`
  width: 100%;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  padding: 7px 9px;
  font-size: 13px;
  background: #fff;
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-height: 40vh;
  overflow: auto;
`;

const Row = styled.button`
  text-align: left;
  border: 1px solid #e2e8f0;
  background: #fff;
  border-radius: 8px;
  padding: 7px 9px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  &:hover {
    background: #f8fafc;
  }
`;

const Dot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex: none;
  background: ${(p) => (p.$mapped ? "#16a34a" : "#d97706")};
`;

const RowText = styled.div`
  min-width: 0;
  flex: 1;
  strong {
    display: block;
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  span {
    display: block;
    font-size: 11px;
    color: #64748b;
  }
`;

const Button = styled.button`
  border: 1px solid #cbd5e1;
  background: #fff;
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  color: #0f172a;
  &:disabled {
    opacity: 0.55;
    cursor: default;
  }
`;

const Primary = styled(Button)`
  background: #1e40af;
  border-color: #1e40af;
  color: #fff;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px;
`;

const Notice = styled.div`
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 12px;
  background: ${(p) => p.$bg || "#f1f5f9"};
  color: ${(p) => p.$fg || "#334155"};
`;

const Toggle = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  cursor: pointer;
`;

const WordAddinPane = () => {
  const [office, setOffice] = useState(null); // { host, platform }
  const [officeError, setOfficeError] = useState("");
  const [connected, setConnected] = useState(Boolean(getTokens()));
  const [connecting, setConnecting] = useState(false);
  const [ctx, setCtx] = useState(null);
  const [ctxError, setCtxError] = useState("");
  const [loadingCtx, setLoadingCtx] = useState(false);
  const [pickedTemplate, setPickedTemplate] = useState("");
  const [tab, setTab] = useState("fields");
  const [query, setQuery] = useState("");
  const [newField, setNewField] = useState("");
  const [signer, setSigner] = useState(1);
  const [revealed, setRevealed] = useState(false);
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState("");
  const [flash, setFlash] = useState(null);
  const [confirmingPublish, setConfirmingPublish] = useState(false);

  const filename = useMemo(() => (office ? filenameFromUrl(documentUrl()) : ""), [office]);

  useEffect(() => {
    loadOffice()
      .then(setOffice)
      .catch((err) => setOfficeError(err.message));
  }, []);

  const say = useCallback((text, tone = "ok") => {
    setFlash({ text, tone });
    window.clearTimeout(say.timer);
    say.timer = window.setTimeout(() => setFlash(null), 3500);
  }, []);

  const loadContext = useCallback(
    async (formstackId) => {
      setLoadingCtx(true);
      setCtxError("");
      try {
        const params = { filename };
        if (formstackId) params.formstack_id = formstackId;
        const res = await fetchContext(params);
        setCtx(res.data);
      } catch (err) {
        if (isAuthError(err)) {
          setTokens(null);
          setConnected(false);
        } else {
          setCtxError(err?.response?.data?.error || "WHG Docs did not answer.");
        }
      } finally {
        setLoadingCtx(false);
      }
    },
    [filename]
  );

  useEffect(() => {
    if (office && connected) loadContext();
  }, [office, connected, loadContext]);

  const refreshSummary = useCallback(async () => {
    try {
      const tags = await scanTags();
      setSummary(summarizeTags(tags));
    } catch (err) {
      // Scanning is a convenience; never block the pane on it.
    }
  }, []);

  useEffect(() => {
    if (office && isWord(office)) refreshSummary();
  }, [office, refreshSummary]);

  const connect = useCallback(async () => {
    setConnecting(true);
    try {
      const raw = await openDialog(`${window.location.origin}/word-addin/connect`);
      const msg = typeof raw === "string" ? JSON.parse(raw) : raw;
      if (msg?.type === "tokens" && msg.access) {
        setTokens({ access: msg.access, refresh: msg.refresh, user: msg.user });
        setConnected(true);
        say(`Connected as ${msg.user?.name || "you"}.`);
      } else {
        say("Sign-in did not complete.", "warn");
      }
    } catch (err) {
      say(err.message || "Sign-in did not complete.", "warn");
    } finally {
      setConnecting(false);
    }
  }, [say]);

  const insertField = useCallback(
    async (name) => {
      const tag = fieldTag(name);
      setBusy(tag);
      try {
        await insertAtCursor(tag, {
          highlight: revealed ? ctx?.reveal_highlights?.fields : null,
        });
        say(`Inserted ${tag}`);
        refreshSummary();
      } catch (err) {
        say("Could not insert — click where the tag should go, then try again.", "warn");
      } finally {
        setBusy("");
      }
    },
    [ctx, refreshSummary, revealed, say]
  );

  const insertNewField = useCallback(async () => {
    const name = cleanFieldName(newField);
    if (!name) return;
    await insertField(name);
    setNewField("");
  }, [insertField, newField]);

  const insertSignature = useCallback(
    async (type) => {
      const tag = signatureTag(type, signer);
      setBusy(tag);
      try {
        await insertAtCursor(tag, {
          color: SIGNATURE_TEXT_COLOR,
          highlight: revealed ? ctx?.reveal_highlights?.signatures : null,
        });
        say(`Inserted ${tag} (white text — turn on Reveal tags to see it)`);
        refreshSummary();
      } catch (err) {
        say("Could not insert — click where the tag should go, then try again.", "warn");
      } finally {
        setBusy("");
      }
    },
    [ctx, refreshSummary, revealed, say, signer]
  );

  const toggleReveal = useCallback(
    async (on) => {
      setBusy("reveal");
      try {
        const n = await setReveal(on, ctx?.reveal_highlights || {});
        setRevealed(on);
        say(on ? `Revealed ${n} tag${n === 1 ? "" : "s"}.` : "Tags hidden again.");
      } catch (err) {
        say("Could not change the highlighting.", "warn");
      } finally {
        setBusy("");
      }
    },
    [ctx, say]
  );

  // Word on the web blocks the browser's own confirm()/alert() inside an
  // add-in pane (they return immediately, as if cancelled), so the
  // confirmation is drawn in the pane instead.
  const publish = useCallback(async () => {
    if (!ctx?.template) return;
    setConfirmingPublish(false);
    setBusy("publish");
    try {
      await publishDraft(ctx.template.formstack_id);
      say("Published. Formstack now has this version.");
    } catch (err) {
      if (isAuthError(err)) {
        setTokens(null);
        setConnected(false);
      } else {
        say(err?.response?.data?.error || "Publish failed — Formstack still has the previous version.", "warn");
      }
    } finally {
      setBusy("");
    }
  }, [ctx, say]);

  // ---- render ----

  if (officeError) {
    return (
      <Pane>
        <Brand>WHG Docs</Brand>
        <Notice $bg="#fef2f2" $fg="#991b1b">{officeError}</Notice>
      </Pane>
    );
  }
  if (!office) {
    return (
      <Pane>
        <Brand>WHG Docs</Brand>
        <Small>Starting…</Small>
      </Pane>
    );
  }
  if (!isWord(office)) {
    return (
      <Pane data-testid="addin-not-in-word">
        <Brand>WHG Docs</Brand>
        <Notice>
          This page is the WHG Docs panel for Microsoft Word. Open a Word template from WHG Docs and use the WHG Docs
          button on Word&apos;s Home ribbon.
        </Notice>
      </Pane>
    );
  }

  if (!connected) {
    return (
      <Pane data-testid="addin-connect">
        <Brand>WHG Docs</Brand>
        <Notice>Sign in once to load this template&apos;s fields and signers.</Notice>
        <Primary type="button" onClick={connect} disabled={connecting} data-testid="addin-connect-button">
          {connecting ? "Waiting for sign-in…" : "Connect to WHG Docs"}
        </Primary>
        {flash && <Notice $bg={flash.tone === "warn" ? "#fffbeb" : "#ecfdf5"}>{flash.text}</Notice>}
      </Pane>
    );
  }

  const template = ctx?.template;
  const fields = filterFields(ctx?.fields, query);

  return (
    <Pane data-testid="addin-pane">
      <Brand>
        <span>WHG Docs</span>
        {template && (
          <a
            href={`${window.location.origin}${template.editor_path}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ fontSize: 11, fontWeight: 600 }}
          >
            Open in WHG Docs ↗
          </a>
        )}
      </Brand>

      {loadingCtx && <Small>Loading the template…</Small>}
      {ctxError && <Notice $bg="#fef2f2" $fg="#991b1b">{ctxError}</Notice>}

      {ctx && !ctx.matched && (
        <Section data-testid="addin-picker">
          <SectionTitle>Which template is this?</SectionTitle>
          <Small>
            This file isn&apos;t one WHG Docs placed in SharePoint{filename ? ` (${filename})` : ""}. Pick the template it
            belongs to, or just use the signature tools below.
          </Small>
          <Select value={pickedTemplate} onChange={(e) => setPickedTemplate(e.target.value)} data-testid="addin-template-select">
            <option value="">Choose a Word template…</option>
            {(ctx.templates || []).map((t) => (
              <option key={t.formstack_id} value={t.formstack_id}>
                {t.name}
              </option>
            ))}
          </Select>
          <Button type="button" disabled={!pickedTemplate} onClick={() => loadContext(pickedTemplate)}>
            Use this template
          </Button>
        </Section>
      )}

      {template && (
        <Small data-testid="addin-template-name">
          <strong style={{ color: "#0f172a" }}>{template.name}</strong>
        </Small>
      )}

      <Section>
        <Toggle>
          <input
            type="checkbox"
            checked={revealed}
            disabled={busy === "reveal"}
            onChange={(e) => toggleReveal(e.target.checked)}
            data-testid="addin-reveal"
          />
          Reveal tags
        </Toggle>
        <Small data-testid="addin-summary">
          {summary
            ? `In this document: ${summary.field} field tag${summary.field === 1 ? "" : "s"} · ${summary.signature} signature tag${
                summary.signature === 1 ? "" : "s"
              }${summary.signers.length ? ` (signer ${summary.signers.join(", ")})` : ""} · ${summary.conditional} condition marker${
                summary.conditional === 1 ? "" : "s"
              }`
            : "Scanning the document…"}
        </Small>
        <Small>Colours are a view aid only — WHG Docs removes them when you publish.</Small>
      </Section>

      <Tabs>
        <Tab type="button" $active={tab === "fields"} onClick={() => setTab("fields")} data-testid="addin-tab-fields">
          Fields
        </Tab>
        <Tab type="button" $active={tab === "signatures"} onClick={() => setTab("signatures")} data-testid="addin-tab-signatures">
          Signatures
        </Tab>
      </Tabs>

      {tab === "fields" && (
        <Section>
          <Small>Click in the document where the value should appear, then click a field.</Small>
          <Input
            placeholder="Search fields…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            data-testid="addin-field-search"
          />
          <List>
            {fields.map((f) => (
              <Row key={f.field_name} type="button" onClick={() => insertField(f.field_name)} disabled={Boolean(busy)} data-testid="addin-field-row">
                <Dot $mapped={f.mapped} />
                <RowText>
                  <strong>{f.field_label || f.field_name}</strong>
                  <span>{f.mapped ? `${f.source} → ${f.sf_field_name}` : "Not mapped yet — map it in WHG Docs after publishing"}</span>
                </RowText>
              </Row>
            ))}
            {ctx?.matched && fields.length === 0 && <Small>No fields match.</Small>}
          </List>
          <SectionTitle>New field</SectionTitle>
          <div style={{ display: "flex", gap: 6 }}>
            <Input
              placeholder="e.g. Spouse Full Name"
              value={newField}
              onChange={(e) => setNewField(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && insertNewField()}
              data-testid="addin-new-field"
            />
            <Button type="button" onClick={insertNewField} disabled={!cleanFieldName(newField) || Boolean(busy)} data-testid="addin-insert-new-field">
              Insert
            </Button>
          </div>
          {cleanFieldName(newField) && <Small>Will insert {fieldTag(cleanFieldName(newField))}</Small>}
        </Section>
      )}

      {tab === "signatures" && (
        <Section>
          <Small>Click where the signer should sign, pick who, then what to place. Tags are written in white so they never print.</Small>
          <Select value={signer} onChange={(e) => setSigner(Number(e.target.value))} data-testid="addin-signer-select">
            {(ctx?.signers || [{ signer: 1, label: "Client" }, { signer: 2, label: "Attorney" }]).map((s) => (
              <option key={s.signer} value={s.signer}>
                {s.label} (signer {s.signer})
              </option>
            ))}
          </Select>
          <Grid>
            {(ctx?.tag_types || [
              { type: "sig", label: "Signature" },
              { type: "date", label: "Date signed" },
              { type: "initial", label: "Initials" },
              { type: "fullname", label: "Printed name" },
            ]).map((t) => (
              <Button key={t.type} type="button" onClick={() => insertSignature(t.type)} disabled={Boolean(busy)} data-testid={`addin-sig-${t.type}`}>
                {t.label}
              </Button>
            ))}
          </Grid>
          <Small>Will insert {signatureTag("sig", signer)} and friends. Signer 1 is always the client.</Small>
        </Section>
      )}

      {template && (
        <Section>
          <SectionTitle>When you&apos;re done</SectionTitle>
          <Small>Word saves as you go. Publishing hands the saved file to Formstack so new documents use it.</Small>
          {confirmingPublish ? (
            <Notice data-testid="addin-publish-confirm">
              <div style={{ marginBottom: 8 }}>
                Publish <strong>{template.name}</strong> to Formstack? Make sure Word shows &quot;Saved&quot; first. Every
                document generated from now on will use this version.
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <Primary type="button" onClick={publish} data-testid="addin-publish-yes">
                  Yes, publish
                </Primary>
                <Button type="button" onClick={() => setConfirmingPublish(false)} data-testid="addin-publish-no">
                  Cancel
                </Button>
              </div>
            </Notice>
          ) : (
            <Primary
              type="button"
              onClick={() => setConfirmingPublish(true)}
              disabled={busy === "publish"}
              data-testid="addin-publish"
            >
              {busy === "publish" ? "Publishing…" : "Publish to Formstack"}
            </Primary>
          )}
        </Section>
      )}

      {flash && (
        <Notice $bg={flash.tone === "warn" ? "#fffbeb" : "#ecfdf5"} $fg={flash.tone === "warn" ? "#92400e" : "#065f46"} data-testid="addin-flash">
          {flash.text}
        </Notice>
      )}
    </Pane>
  );
};

export default WordAddinPane;
